import { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTH_URL = `${API_URL}/authRoutes`;

const cleanPhone = (raw) => raw.replace(/\D/g, '').slice(-10);

const RESEND_COOLDOWN = 30; // seconds

const Login = () => {
  const { login, loginWithOtp } = useContext(AuthContext);
  const navigate = useNavigate();

  // 'password' (default) | 'otp'
  const [mode, setMode] = useState('password');

  // ── OTP login state ──────────────────────────────────────
  const [otpPhone, setOtpPhone]       = useState('');
  const [otpStep, setOtpStep]         = useState('phone'); // 'phone' | 'verify'
  const [otpCode, setOtpCode]         = useState('');
  const [phoneHint, setPhoneHint]     = useState('');
  const [resendSecs, setResendSecs]   = useState(0);
  const resendTimer = useRef(null);

  // ── Password login state ─────────────────────────────────
  const [username, setUsername]       = useState('');
  const [password, setPassword]       = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // ── Forgot-password state ────────────────────────────────
  const [fpStep, setFpStep]           = useState(null); // null | 'request' | 'verify'
  const [fpIdentifier, setFpIdentifier] = useState('');
  const [fpOtp, setFpOtp]             = useState('');
  const [fpNewPwd, setFpNewPwd]       = useState('');
  const [fpConfirm, setFpConfirm]     = useState('');
  const [fpPhoneHint, setFpPhoneHint] = useState('');
  const [showFpPwd, setShowFpPwd]     = useState(false);

  // ── Shared ───────────────────────────────────────────────
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [successMsg, setSuccessMsg]   = useState('');

  useEffect(() => () => clearInterval(resendTimer.current), []);

  const startResendTimer = () => {
    setResendSecs(RESEND_COOLDOWN);
    resendTimer.current = setInterval(() => {
      setResendSecs(s => {
        if (s <= 1) { clearInterval(resendTimer.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const resetOtpFlow = () => {
    setOtpStep('phone');
    setOtpCode('');
    setPhoneHint('');
    clearInterval(resendTimer.current);
    setResendSecs(0);
  };

  const afterLogin = (userData) => {
    if (!userData.profile_completed && userData.user_type !== 'S') {
      navigate('/know-you-better', { replace: true });
    } else {
      navigate('/categories', { replace: true });
    }
  };

  // ── OTP handlers ─────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    const phone = cleanPhone(otpPhone);
    if (phone.length !== 10) { setError('Enter a valid 10-digit phone number.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${AUTH_URL}/otp-login/request`, { phone });
      setPhoneHint(res.data.phone_hint);
      setOtpStep('verify');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) { setError('Enter the 6-digit OTP.'); return; }
    setError(''); setLoading(true);
    try {
      const userData = await loginWithOtp(cleanPhone(otpPhone), otpCode);
      afterLogin(userData);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSecs > 0) return;
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${AUTH_URL}/otp-login/request`, { phone: cleanPhone(otpPhone) });
      setPhoneHint(res.data.phone_hint);
      setOtpCode('');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  // ── Password handler ──────────────────────────────────────
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');  setLoading(true);
    try {
      const userData = await login(username, password);
      afterLogin(userData);
    } catch (err) {
      setError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot-password handlers ──────────────────────────────
  const handleFpRequest = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${AUTH_URL}/forgot-password/request`, { identifier: fpIdentifier });
      setFpPhoneHint(res.data.phone_hint);
      setFpStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFpReset = async (e) => {
    e.preventDefault();
    setError('');
    if (fpNewPwd !== fpConfirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${AUTH_URL}/forgot-password/reset`, {
        identifier: fpIdentifier, otp: fpOtp, new_password: fpNewPwd,
      });
      setSuccessMsg('Password reset successfully! You can now sign in.');
      setFpStep(null); setFpIdentifier(''); setFpOtp(''); setFpNewPwd(''); setFpConfirm('');
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelFp = () => {
    setFpStep(null); setError('');
    setFpIdentifier(''); setFpOtp(''); setFpNewPwd(''); setFpConfirm('');
  };

  const switchMode = (m) => {
    setMode(m); setError(''); setSuccessMsg('');
    resetOtpFlow();
  };

  // ─────────────────────────────────────────────────────────
  return (
    <div className="login-container">
      <div className="login-background">
        <div className="background-shape shape-1"></div>
        <div className="background-shape shape-2"></div>
        <div className="background-shape shape-3"></div>
      </div>

      <div className="login-card">
        <div className="login-header">
          <img src="/logo.webp" alt="The New Luxury" className="login-logo" />
        </div>

        {/* ── Forgot-password overlay ── */}
        {fpStep !== null ? (
          <>
            {fpStep === 'request' && (
              <>
                <div className="welcome-text">
                  <h2>Forgot Password</h2>
                  <p>We'll send an OTP to your registered WhatsApp</p>
                </div>
                {error && <div className="error-message"><span>{error}</span></div>}
                <form onSubmit={handleFpRequest} className="login-form">
                  <div className="form-group">
                    <label>Phone / Email / Username</label>
                    <div className="input-wrapper">
<input type="text" value={fpIdentifier}
                        onChange={e => setFpIdentifier(e.target.value)}
                        placeholder="Phone, email or username" required disabled={loading}
                        autoComplete="username" />
                    </div>
                  </div>
                  <button type="submit" className="login-button login-button--wa" disabled={loading || !fpIdentifier}>
                    {loading ? <><span className="spinner"></span><span>Sending…</span></> : 'Send OTP via WhatsApp'}
                  </button>
                  <button type="button" className="fp-back-btn" onClick={cancelFp}>← Back to Sign In</button>
                </form>
              </>
            )}

            {fpStep === 'verify' && (
              <>
                <div className="welcome-text">
                  <h2>Reset Password</h2>
                  <p>OTP sent to <strong>{fpPhoneHint}</strong> via WhatsApp</p>
                </div>
                {error && <div className="error-message"><span>{error}</span></div>}
                <form onSubmit={handleFpReset} className="login-form">
                  <div className="form-group">
                    <label>OTP</label>
                    <div className="input-wrapper">
<input type="text" inputMode="numeric" maxLength={6}
                        value={fpOtp} onChange={e => setFpOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder="6-digit OTP" required disabled={loading} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <div className="input-wrapper">
<input type={showFpPwd ? 'text' : 'password'}
                        value={fpNewPwd} onChange={e => setFpNewPwd(e.target.value)}
                        placeholder="New password (min 4 chars)" required disabled={loading}
                        autoComplete="new-password" />
                      <button type="button" className="password-toggle"
                        onClick={() => setShowFpPwd(!showFpPwd)} tabIndex={-1}>
                        {showFpPwd ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <div className="input-wrapper">
<input type={showFpPwd ? 'text' : 'password'}
                        value={fpConfirm} onChange={e => setFpConfirm(e.target.value)}
                        placeholder="Repeat new password" required disabled={loading}
                        autoComplete="new-password" />
                    </div>
                  </div>
                  <button type="submit" className="login-button"
                    disabled={loading || !fpOtp || !fpNewPwd || !fpConfirm}>
                    {loading ? <><span className="spinner"></span><span>Resetting…</span></> : 'Reset Password'}
                  </button>
                  <button type="button" className="fp-back-btn"
                    onClick={() => { setFpStep('request'); setError(''); setFpOtp(''); setFpNewPwd(''); setFpConfirm(''); }}>
                    ← Resend OTP
                  </button>
                </form>
              </>
            )}
          </>
        ) : (
          <>
            {/* ── Mode tabs ── */}
            <div className="login-tabs">
              <button
                className={`login-tab${mode === 'password' ? ' login-tab--active' : ''}`}
                onClick={() => switchMode('password')}
                type="button"
              >
                Password
              </button>
              <button
                className={`login-tab${mode === 'otp' ? ' login-tab--active' : ''}`}
                onClick={() => switchMode('otp')}
                type="button"
              >
                WhatsApp OTP
              </button>
            </div>

            {error    && <div className="error-message"><span className="error-icon">⚠️</span><span>{error}</span></div>}
            {successMsg && <div className="fp-success-msg"><span>{successMsg}</span></div>}

            {/* ── WhatsApp OTP mode ── */}
            {mode === 'otp' && otpStep === 'phone' && (
              <>
                <div className="welcome-text">
                  <h2>Welcome Back!</h2>
                  <p>We'll send a one-time code to your WhatsApp</p>
                </div>
                <form onSubmit={handleSendOtp} className="login-form">
                  <div className="form-group">
                    <label htmlFor="otp-phone">Phone Number</label>
                    <div className="input-wrapper">
<input
                        id="otp-phone"
                        type="tel"
                        inputMode="numeric"
                        value={otpPhone}
                        onChange={e => setOtpPhone(e.target.value)}
                        placeholder="10-digit mobile number"
                        required
                        disabled={loading}
                        autoComplete="tel"
                        maxLength={13}
                      />
                    </div>
                  </div>
                  <button type="submit" className="login-button login-button--wa"
                    disabled={loading || cleanPhone(otpPhone).length !== 10}>
                    {loading
                      ? <><span className="spinner"></span><span>Sending OTP…</span></>
                      : 'Send OTP via WhatsApp'}
                  </button>
                </form>
              </>
            )}

            {mode === 'otp' && otpStep === 'verify' && (
              <>
                <div className="welcome-text">
                  <h2>Enter OTP</h2>
                  <p>Sent to <strong>{phoneHint}</strong> on WhatsApp</p>
                </div>
                <form onSubmit={handleVerifyOtp} className="login-form">
                  <div className="form-group">
                    <label htmlFor="otp-code">One-Time Password</label>
                    <div className="input-wrapper">
<input
                        id="otp-code"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otpCode}
                        onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="6-digit OTP"
                        required
                        disabled={loading}
                        autoFocus
                      />
                    </div>
                  </div>
                  <button type="submit" className="login-button login-button--wa"
                    disabled={loading || otpCode.length !== 6}>
                    {loading
                      ? <><span className="spinner"></span><span>Verifying…</span></>
                      : 'Verify & Sign In'}
                  </button>
                  <div className="otp-resend-row">
                    <button type="button" className="fp-back-btn" onClick={() => { resetOtpFlow(); setError(''); }}>
                      ← Change number
                    </button>
                    <button
                      type="button"
                      className={`resend-btn${resendSecs > 0 ? ' resend-btn--waiting' : ''}`}
                      onClick={handleResendOtp}
                      disabled={resendSecs > 0 || loading}
                    >
                      {resendSecs > 0 ? `Resend in ${resendSecs}s` : 'Resend OTP'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* ── Password mode ── */}
            {mode === 'password' && (
              <>
                <div className="welcome-text">
                  <h2>Welcome Back!</h2>
                  <p>Sign in with your username and password</p>
                </div>
                <form onSubmit={handlePasswordLogin} className="login-form">
                  <div className="form-group">
                    <label htmlFor="username">Phone / Email / Username</label>
                    <div className="input-wrapper">
<input type="text" id="username" value={username}
                        onChange={e => setUsername(e.target.value)}
                        placeholder="Phone, email or username" required disabled={loading}
                        autoComplete="username" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="input-wrapper">
<input type={showPassword ? 'text' : 'password'} id="password"
                        value={password} onChange={e => setPassword(e.target.value)}
                        placeholder="Enter your password" required disabled={loading}
                        autoComplete="current-password" />
                      <button type="button" className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading} tabIndex={-1}>
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  <div className="fp-link-row">
                    <button type="button" className="fp-link"
                      onClick={() => { setError(''); setSuccessMsg(''); setFpStep('request'); }}>
                      Forgot password?
                    </button>
                  </div>
                  <button type="submit" className="login-button" disabled={loading}>
                    {loading ? <><span className="spinner"></span><span>Signing in…</span></> : 'Sign In'}
                  </button>
                </form>
              </>
            )}

            <div className="login-register">
              New here?{' '}
              <button className="login-register-link" onClick={() => navigate('/register')}>
                Create an account
              </button>
            </div>
          </>
        )}

        <div className="login-footer">
          <p>© {new Date().getFullYear()} The New Luxury. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
